import { AssignmentType } from '../../types';

export interface AssignmentLevelsResult {
  assignmentLevels: Map<number, number>;
  maxIntersectedAssignments: number;
}

const EMPTY_LEVELS_RESULT: AssignmentLevelsResult = {
  assignmentLevels: new Map<number, number>(),
  maxIntersectedAssignments: 1,
};

interface NormalizedInterval {
  startTime: number;
  endTime: number;
}

/**
 * Inclusive overlap: two day-level intervals overlap when they share or touch a
 * day boundary (matches the legacy `startTime <= endTime` semantics — bars on the
 * same day separate onto different levels rather than collapsing onto one).
 */
const intervalsOverlap = (a: NormalizedInterval, b: NormalizedInterval): boolean =>
  a.startTime <= b.endTime && b.startTime <= a.endTime;

/**
 * Calculates assignment levels for optimal vertical positioning.
 *
 * Ordering is ALPHABETICAL by project name (`title`) first so the vertical lane a
 * project occupies is predictable and consistent across the whole row — within any
 * set of mutually-overlapping assignments they read top-to-bottom A→Z. Stable
 * tiebreakers (startTime, endTime, id) keep the result deterministic across date
 * segments (split by time-offs/weekends) and across refetches/merges.
 *
 * Greedy lowest-free-level packing is preserved so non-overlapping assignments
 * still share lanes (the row height does not blow up).
 *
 * IMPORTANT: because the name-first sort makes start times non-monotonic, we can no
 * longer track just the LAST end date per level (the previous approach, valid only
 * for start-sorted input — it would silently double-stack overlapping bars here).
 * Each level keeps the FULL list of its assigned intervals and a candidate is tested
 * against every one — correct for any input order.
 *
 * Note: WeakMap caching was removed as it caused memory leaks. Arrays are recreated
 * on every render/data fetch. Per-row assignment counts are small, so the algorithm
 * is fast enough without caching.
 */
export const calculateAssignmentLevels = (
  assignments: AssignmentType[]
): AssignmentLevelsResult => {
  if (assignments.length === 0) {
    return EMPTY_LEVELS_RESULT;
  }

  const assignmentLevels = new Map<number, number>();
  // Per-level list of every interval already placed on that level.
  const levelIntervals: NormalizedInterval[][] = [];

  const normalizedAssignments = assignments
    .map(assignment => ({
      assignment,
      startTime: assignment.startDate.startOf('day').valueOf(),
      endTime: assignment.endDate.startOf('day').valueOf(),
    }))
    .sort((a, b) => {
      // 1. Alphabetical by project name (case-insensitive, locale-aware) so lanes
      //    read A→Z top-to-bottom and stay consistent across the whole timeline.
      const byName = a.assignment.title.localeCompare(b.assignment.title, undefined, {
        sensitivity: 'base',
      });
      if (byName !== 0) return byName;
      // 2-4. Deterministic tiebreakers so a fragment always lands on the same level
      //      across date segments and across refetches/merges.
      if (a.startTime !== b.startTime) return a.startTime - b.startTime;
      if (a.endTime !== b.endTime) return a.endTime - b.endTime;
      return a.assignment.originalAssignment.id - b.assignment.originalAssignment.id;
    });

  for (const item of normalizedAssignments) {
    const candidate: NormalizedInterval = { startTime: item.startTime, endTime: item.endTime };

    // Lowest level whose ALL placed intervals are free of this candidate.
    let level = 0;
    while (
      level < levelIntervals.length &&
      levelIntervals[level].some(existing => intervalsOverlap(existing, candidate))
    ) {
      level++;
    }

    if (level === levelIntervals.length) {
      levelIntervals.push([]);
    }
    levelIntervals[level].push(candidate);
    assignmentLevels.set(item.assignment.originalAssignment.id, level);
  }

  return {
    assignmentLevels,
    maxIntersectedAssignments: Math.max(1, levelIntervals.length),
  };
};
